import { createSlice } from '@reduxjs/toolkit';
import { callApi } from 'src/utils/apiCall';
import { hasFeatureAccess } from 'src/utils/featurePermission';
import { dispatch } from '../store';
import { updateError } from './user';

const initialState = {
    isLoading: false,
    error: null,
    kpiData: null,
    practitioners: [],
    treatmentMix: [],
    appointmentKpis: null,
    lastSynced: null,
    isRefreshing: false,
    isSyncing: false,
};

const slice = createSlice({
    name: 'dentallyReporting',
    initialState,
    reducers: {
        startLoading(state) {
            state.isLoading = true;
            state.error = null;
        },
        hasError(state, action) {
            state.isLoading = false;
            state.error = action.payload;
        },
        getKPIsSuccess(state, action) {
            state.isLoading = false;
            state.isSyncing = false;
            state.kpiData = action.payload;
            state.lastSynced = action.payload?.generated_at || null;
        },
        getPractitionersSuccess(state, action) {
            state.practitioners = action.payload;
        },
        getTreatmentMixSuccess(state, action) {
            state.treatmentMix = action.payload;
        },
        getAppointmentKpisSuccess(state, action) {
            state.appointmentKpis = action.payload;
        },
        setRefreshing(state, action) {
            state.isRefreshing = action.payload;
        },
        setSyncing(state, action) {
            state.isSyncing = action.payload;
        },
        resetState(state) {
            Object.assign(state, initialState);
        },
    },
});

export default slice.reducer;

// --- Helpers ---

const POLL_INTERVAL = 3000; // 3 seconds
const POLL_MAX_DURATION = 60000; // 60 seconds

// Module-level timer ID so we can cancel polling on unmount/reset
let _pollTimerId = null;

function _clearPollTimer() {
    if (_pollTimerId) {
        clearTimeout(_pollTimerId);
        _pollTimerId = null;
    }
}

function pollForKPIData(practiceId, params, startTime = Date.now()) {
    return async () => {
        if (!hasFeatureAccess('dentally-integration')) return;
        const queryParams = new URLSearchParams();
        if (params.period) queryParams.set('period', params.period);
        if (params.date_from) queryParams.set('date_from', params.date_from);
        if (params.date_to) queryParams.set('date_to', params.date_to);

        const qs = queryParams.toString();
        const url = `/api/v1/practices/${practiceId}/dentally-reporting/kpis/${qs ? `?${qs}` : ''}`;
        const response = await callApi(url);

        if (response.status === 200) {
            dispatch(slice.actions.getKPIsSuccess(response.data));
            return;
        }

        if (response.status === 202 && (Date.now() - startTime) < POLL_MAX_DURATION) {
            // Still syncing — poll again after interval
            _pollTimerId = setTimeout(() => {
                dispatch(pollForKPIData(practiceId, params, startTime));
            }, POLL_INTERVAL);
            return;
        }

        // Timeout or unexpected status
        if (response.status === 202) {
            dispatch(slice.actions.setSyncing(false));
            dispatch(slice.actions.hasError('Data sync is taking longer than expected. Please try again.'));
        } else if (response.status === 403) {
            dispatch(slice.actions.hasError(response.data?.detail || 'Dentally integration not enabled'));
        } else {
            dispatch(slice.actions.hasError(response.data?.detail || 'Failed to fetch KPI data'));
        }
    };
}

// --- Thunks ---

export function fetchDentallyKPIs(practiceId, params = {}) {
    return async () => {
        if (!hasFeatureAccess('dentally-integration')) return;
        try {
            _clearPollTimer();
            dispatch(slice.actions.startLoading());
            const queryParams = new URLSearchParams();
            if (params.period) queryParams.set('period', params.period);
            if (params.date_from) queryParams.set('date_from', params.date_from);
            if (params.date_to) queryParams.set('date_to', params.date_to);

            const qs = queryParams.toString();
            const url = `/api/v1/practices/${practiceId}/dentally-reporting/kpis/${qs ? `?${qs}` : ''}`;
            const response = await callApi(url);

            if (response.status === 200) {
                dispatch(slice.actions.getKPIsSuccess(response.data));
            } else if (response.status === 202) {
                // Backend is syncing this period — start polling
                dispatch(slice.actions.setSyncing(true));
                _pollTimerId = setTimeout(() => {
                    dispatch(pollForKPIData(practiceId, params));
                }, POLL_INTERVAL);
            } else if (response.status === 404) {
                dispatch(slice.actions.getKPIsSuccess(null));
            } else if (response.status === 403) {
                dispatch(slice.actions.hasError(response.data?.detail || 'Dentally integration not enabled'));
            } else {
                dispatch(slice.actions.hasError(response.data?.detail || 'Failed to fetch KPI data'));
            }
        } catch (err) {
            dispatch(slice.actions.hasError(err.message || 'Something went wrong'));
        }
    };
}

export function fetchDentallyPractitioners(practiceId, params = {}) {
    return async () => {
        if (!hasFeatureAccess('dentally-integration')) return;
        try {
            const queryParams = new URLSearchParams();
            if (params.period) queryParams.set('period', params.period);
            if (params.date_from) queryParams.set('date_from', params.date_from);
            if (params.date_to) queryParams.set('date_to', params.date_to);

            const qs = queryParams.toString();
            const url = `/api/v1/practices/${practiceId}/dentally-reporting/kpis/practitioners/${qs ? `?${qs}` : ''}`;
            const response = await callApi(url);

            if (response.status === 200) {
                dispatch(slice.actions.getPractitionersSuccess(response.data));
            }
        } catch (err) {
            console.error('Failed to fetch practitioner KPIs:', err);
        }
    };
}

export function fetchDentallyTreatmentMix(practiceId, params = {}) {
    return async () => {
        if (!hasFeatureAccess('dentally-integration')) return;
        try {
            const queryParams = new URLSearchParams();
            if (params.period) queryParams.set('period', params.period);
            if (params.date_from) queryParams.set('date_from', params.date_from);
            if (params.date_to) queryParams.set('date_to', params.date_to);

            const qs = queryParams.toString();
            const url = `/api/v1/practices/${practiceId}/dentally-reporting/kpis/treatment-mix/${qs ? `?${qs}` : ''}`;
            const response = await callApi(url);

            if (response.status === 200) {
                dispatch(slice.actions.getTreatmentMixSuccess(response.data));
            }
        } catch (err) {
            console.error('Failed to fetch treatment mix:', err);
        }
    };
}

export function fetchDentallyAppointmentKpis(practiceId, params = {}) {
    return async () => {
        if (!hasFeatureAccess('dentally-integration')) return;
        try {
            const queryParams = new URLSearchParams();
            if (params.period) queryParams.set('period', params.period);
            if (params.date_from) queryParams.set('date_from', params.date_from);
            if (params.date_to) queryParams.set('date_to', params.date_to);

            const qs = queryParams.toString();
            const url = `/api/v1/practices/${practiceId}/dentally-reporting/kpis/appointments/${qs ? `?${qs}` : ''}`;
            const response = await callApi(url);

            if (response.status === 200) {
                dispatch(slice.actions.getAppointmentKpisSuccess(response.data));
            }
        } catch (err) {
            console.error('Failed to fetch appointment KPIs:', err);
        }
    };
}

export function triggerDentallyRefresh(practiceId, params = {}) {
    // Support legacy string period argument
    const resolvedParams = typeof params === 'string' ? { period: params } : params;
    return async () => {
        if (!hasFeatureAccess('dentally-integration')) return;
        try {
            dispatch(slice.actions.setRefreshing(true));
            const response = await callApi(
                `/api/v1/practices/${practiceId}/dentally-reporting/kpis/refresh/`,
                'POST',
                { period: resolvedParams.period || 'this_month' }
            );

            if (response.status === 202) {
                dispatch(updateError({ success: true, message: 'Dentally data refresh started for all periods' }));
                // Start polling for the current period
                _clearPollTimer();
                dispatch(slice.actions.setSyncing(true));
                _pollTimerId = setTimeout(() => {
                    dispatch(pollForKPIData(practiceId, resolvedParams));
                }, POLL_INTERVAL);
            } else {
                dispatch(updateError({ success: false, message: response.data?.detail || 'Refresh failed' }));
            }
        } catch (err) {
            dispatch(updateError({ success: false, message: err.message || 'Refresh failed' }));
        } finally {
            dispatch(slice.actions.setRefreshing(false));
        }
    };
}

export function resetDentallyReporting() {
    return () => {
        _clearPollTimer();
        dispatch(slice.actions.resetState());
    };
}
